import * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { Button } from "./button";

export type DataTableColumn<T> = {
  title: React.ReactNode;
  dataIndex?: keyof T | string;
  key?: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sortable?: boolean;
  sortKey?: keyof T | string;
  sortFn?: (a: T, b: T) => number;
};

export type DataTablePagination = {
  pageSize?: number;
  position?: Array<"bottomCenter" | "bottomLeft" | "bottomRight">;
  showSizeChanger?: boolean;
};

export type DataTableRowSelection<T> = {
  onChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  selectedRowKeys?: React.Key[];
};

export type DataTableSearch<T> = {
  keys: Array<keyof T | string>;
  placeholder?: string;
  className?: string;
};

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey?: string | ((record: T) => React.Key);
  loading?: boolean;
  pagination?: false | DataTablePagination;
  rowSelection?: DataTableRowSelection<T>;
  onRow?: (
    record: T,
    index: number
  ) => React.HTMLAttributes<HTMLTableRowElement> | undefined;
  size?: "sm" | "md";
  search?: DataTableSearch<T>;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  emptyMessage?: string;
}

const getRowKey = <T,>(
  record: T,
  index: number,
  rowKey?: string | ((record: T) => React.Key)
): React.Key => {
  if (typeof rowKey === "function") return rowKey(record);
  if (typeof rowKey === "string" && (record as any)?.[rowKey] != null) {
    return (record as any)[rowKey] as React.Key;
  }
  return index;
};

export function DataTable<T>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  pagination = { pageSize: 10 },
  rowSelection,
  onRow,
  size = "md",
  search,
  toolbarLeft,
  toolbarRight,
  emptyMessage = "표시할 데이터가 없습니다.",
}: DataTableProps<T>) {
  const [page, setPage] = React.useState(1);
  const [searchText, setSearchText] = React.useState("");
  const [sortState, setSortState] = React.useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  React.useEffect(() => {
    setPage(1);
  }, [searchText, sortState?.key, sortState?.direction]);

  const filteredData = React.useMemo(() => {
    if (!search || !searchText.trim()) return dataSource;
    const needle = searchText.trim().toLowerCase();
    return dataSource.filter((row) => {
      return search.keys.some((key) => {
        const value = (row as any)[key];
        if (value == null) return false;
        return String(value).toLowerCase().includes(needle);
      });
    });
  }, [dataSource, search, searchText]);

  const sortedData = React.useMemo(() => {
    if (!sortState) return filteredData;
    const col = columns.find((c) =>
      String(c.key || c.dataIndex || c.title) === sortState.key
    );
    if (!col) return filteredData;

    const dir = sortState.direction === "asc" ? 1 : -1;
    const getValue = (row: T) => {
      if (col.sortKey) return (row as any)[col.sortKey];
      if (col.dataIndex) return (row as any)[col.dataIndex];
      return undefined;
    };

    return [...filteredData].sort((a, b) => {
      if (col.sortFn) return col.sortFn(a, b) * dir;
      const va = getValue(a);
      const vb = getValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb), "ko") * dir;
    });
  }, [filteredData, sortState, columns]);

  const pageSize =
    pagination === false ? sortedData.length : pagination?.pageSize || 10;
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  React.useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pagedData = React.useMemo(() => {
    if (pagination === false) return sortedData;
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize, pagination]);

  const controlledSelected = rowSelection?.selectedRowKeys;
  const [internalSelected, setInternalSelected] = React.useState<React.Key[]>(
    controlledSelected ?? []
  );

  React.useEffect(() => {
    if (controlledSelected) setInternalSelected(controlledSelected);
  }, [controlledSelected]);

  const selectedKeys = controlledSelected ?? internalSelected;

  const toggleRow = (key: React.Key) => {
    const exists = selectedKeys.includes(key);
    const next = exists
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    if (!controlledSelected) setInternalSelected(next);
    if (rowSelection?.onChange) {
      const selectedRows = sortedData.filter((row, idx) =>
        next.includes(getRowKey(row, idx, rowKey))
      );
      rowSelection.onChange(next, selectedRows);
    }
  };

  const toggleAll = () => {
    const visibleKeys = pagedData.map((row, idx) =>
      getRowKey(row, idx + (page - 1) * pageSize, rowKey)
    );
    const allSelected = visibleKeys.every((k) => selectedKeys.includes(k));
    const next = allSelected
      ? selectedKeys.filter((k) => !visibleKeys.includes(k))
      : Array.from(new Set([...selectedKeys, ...visibleKeys]));
    if (!controlledSelected) setInternalSelected(next);
    if (rowSelection?.onChange) {
      const selectedRows = sortedData.filter((row, idx) =>
        next.includes(getRowKey(row, idx, rowKey))
      );
      rowSelection.onChange(next, selectedRows);
    }
  };

  const handleSortToggle = (col: DataTableColumn<T>) => {
    const colKey = String(col.key || col.dataIndex || col.title);
    if (!col.sortable) return;
    setSortState((prev) => {
      if (!prev || prev.key !== colKey) return { key: colKey, direction: "asc" };
      if (prev.direction === "asc") return { key: colKey, direction: "desc" };
      return null;
    });
  };

  const renderSortIcon = (col: DataTableColumn<T>) => {
    const colKey = String(col.key || col.dataIndex || col.title);
    if (!col.sortable) return null;
    if (!sortState || sortState.key !== colKey) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground" />;
    }
    return sortState.direction === "asc" ? (
      <ArrowUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="ml-1 h-3.5 w-3.5" />
    );
  };

  return (
    <div className="relative space-y-3">
      {(search || toolbarLeft || toolbarRight) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-1 items-center gap-2">
            {search && (
              <div className={cn("relative w-full max-w-sm", search.className)}>
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={search.placeholder || "검색"}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-8"
                />
              </div>
            )}
            {toolbarLeft}
          </div>
          {toolbarRight}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {rowSelection && (
              <TableHead className="w-[36px] text-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-black"
                  checked={
                    pagedData.length > 0 &&
                    pagedData
                      .map((row, idx) =>
                        getRowKey(row, idx + (page - 1) * pageSize, rowKey)
                      )
                      .every((k) => selectedKeys.includes(k))
                  }
                  onChange={toggleAll}
                />
              </TableHead>
            )}
            {columns.map((col) => {
              const colKey = String(col.key || col.dataIndex || col.title);
              return (
                <TableHead
                  key={colKey}
                  style={{ width: col.width, textAlign: col.align }}
                  className={cn(size === "sm" && "h-9 text-xs")}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1"
                      onClick={() => handleSortToggle(col)}
                    >
                      <span>{col.title}</span>
                      {renderSortIcon(col)}
                    </button>
                  ) : (
                    col.title
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (rowSelection ? 1 : 0)}
                className="py-10 text-center text-sm text-muted-foreground"
              >
                불러오는 중...
              </TableCell>
            </TableRow>
          ) : pagedData.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (rowSelection ? 1 : 0)}
                className="py-10 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            pagedData.map((record, index) => {
              const realIndex = index + (page - 1) * pageSize;
              const key = getRowKey(record, realIndex, rowKey);
              const rowProps = onRow?.(record, realIndex) || {};
              return (
                <TableRow key={String(key)} {...rowProps}>
                  {rowSelection && (
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-black"
                        checked={selectedKeys.includes(key)}
                        onChange={() => toggleRow(key)}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => {
                    const value = col.dataIndex
                      ? (record as any)[col.dataIndex]
                      : undefined;
                    return (
                      <TableCell
                        key={String(col.key || col.dataIndex || col.title)}
                        style={{ textAlign: col.align }}
                        className={cn(size === "sm" && "py-2 text-xs")}
                      >
                        {col.render
                          ? col.render(value, record, realIndex)
                          : value ?? "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {pagination !== false && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            이전
          </Button>
          <span className="text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
}
